import { useCallback } from "react";
import { printer as platformPrinter } from "@/platform";
import { useToast } from "@/hooks/use-toast";
import usePrinterConfig from "@/hooks/usePrinterConfig";
import usePrintTicket from "@/hooks/usePrintTicket";
import { TicketData } from "@/types/printer";
import { epsonPrintTicket } from "@/utils/epsonEpos";

interface ThermalPrintOptions {
  ticketData: TicketData;
  htmlContent: string;
}

function useThermalPrint() {
  const { config, isLoaded } = usePrinterConfig();
  const { printTicket: browserPrint } = usePrintTicket();
  const { toast } = useToast();

  const hasThermalPrinter = isLoaded && !!config?.ticketPrinter;

  const printTicket = useCallback(
    async ({ ticketData, htmlContent }: ThermalPrintOptions) => {
      if (!isLoaded) return;

      const printerCfg = config?.ticketPrinter;

      if (!printerCfg) {
        browserPrint(htmlContent);
        return;
      }

      try {
        if (printerCfg.type === "epson") {
          // Epson ePOS: direct from browser via HTTP — works on web too
          const result = await epsonPrintTicket(
            printerCfg.host,
            printerCfg.port,
            ticketData
          );
          if (!result.success) {
            throw new Error(result.error || "Epson ePOS print failed");
          }
        } else {
          // USB/Serial, CUPS, TCP — desktop only via platform layer
          await platformPrinter.printTicket({
            config: printerCfg,
            data: ticketData,
          });
        }
      } catch (err) {
        console.error("Thermal print failed, falling back to browser", err);
        toast({
          title: "Error de impresora",
          description:
            "No se pudo imprimir. Usando impresion del navegador como respaldo.",
          variant: "error",
          duration: 5000,
        });
        browserPrint(htmlContent);
      }
    },
    [isLoaded, config, browserPrint, toast]
  );

  return { printTicket, hasThermalPrinter, isLoaded };
}

export default useThermalPrint;
